import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Mail as MailIcon } from "@mui/icons-material";
import {
  getMyPendingInvitations,
  acceptInvitation,
} from "../../services/Organization";
import { fetchOrganizations, setCurrentOrg } from "../../redux/slices/orgSlice";
import { SPACING } from "../../theme/constants";

const ROLE_COLORS = {
  OWNER: "error",
  ADMIN: "warning",
  DEVELOPER: "primary",
  VIEWER: "default",
};

export default function PendingInvitations() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);

  useEffect(() => {
    getMyPendingInvitations()
      .then((res) => setInvitations(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAccept = async (token, id) => {
    setAccepting(id);
    try {
      const response = await acceptInvitation(token);
      setInvitations((prev) => prev.filter((i) => i.id !== id));

      // Refresh org list and switch to the accepted org
      const orgsResult = await dispatch(fetchOrganizations()).unwrap();
      const acceptedOrg = response.data?.org;
      if (acceptedOrg) {
        const fullOrg = orgsResult.find((o) => o.id === acceptedOrg.id);
        if (fullOrg) {
          dispatch(setCurrentOrg(fullOrg));
          window.location.reload();
        }
      }
    } catch {
      // Invitation page handles errors better — redirect there
      navigate(`/invite/${token}`);
    } finally {
      setAccepting(null);
    }
  };

  if (loading || invitations.length === 0) return null;

  return (
    <Box sx={{ mb: SPACING.xl }}>
      {invitations.map((inv) => {
        const inviterName =
          [inv.inviter?.firstName, inv.inviter?.lastName]
            .filter(Boolean)
            .join(" ") ||
          inv.inviter?.email ||
          "Someone";
        return (
          <Alert
            key={inv.id}
            severity="info"
            icon={<MailIcon />}
            sx={{ mb: SPACING.md, alignItems: "center" }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={accepting === inv.id}
                  onClick={() => handleAccept(inv.token, inv.id)}
                >
                  {accepting === inv.id ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    "Accept"
                  )}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/invite/${inv.token}`)}
                >
                  View
                </Button>
              </Stack>
            }
          >
            <Typography variant="body2">
              <strong>{inviterName}</strong> invited you to join{" "}
              <strong>{inv.org?.name}</strong> as{" "}
              <Chip
                label={inv.role}
                size="small"
                color={ROLE_COLORS[inv.role] || "default"}
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Typography>
          </Alert>
        );
      })}
    </Box>
  );
}
